import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X, ExternalLink, SkipBack, SkipForward, Repeat, Shuffle, Volume2, VolumeX, ListMusic, ChevronUp, ChevronDown, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { usePlayer } from '@/context/PlayerContext';

const PlayerBar = () => {
  const { 
    currentTrack, 
    isPlaying, 
    artist, 
    playlist,
    currentIndex,
    isLooping,
    isShuffled,
    isMuted,
    volume,
    userInteracted,
    pauseTrack, 
    resumeTrack, 
    stopTrack,
    nextTrack,
    prevTrack,
    toggleLoop,
    toggleShuffle,
    toggleMute,
    setVolume,
    setUserInteracted,
    handleTrackEnd
  } = usePlayer();
  
  const [expanded, setExpanded] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [backgroundMode, setBackgroundMode] = useState(false);
  const iframeRef = useRef(null);
  const audioRef = useRef(null);

  // Handle click to start playback (for autoplay policy)
  const handleStartPlayback = () => {
    setUserInteracted(true);
    resumeTrack();
  };

  // Setup Media Session API for background controls (lock screen, notification)
  const updateMediaSession = useCallback(() => {
    if ('mediaSession' in navigator && currentTrack) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack.title,
        artist: artist?.name || currentTrack.artist_name || 'Twin Tube',
        album: 'Twin Tube - Twin Cities Music',
        artwork: [
          { src: currentTrack.thumbnail || '/icons/icon-192x192.png', sizes: '192x192', type: 'image/png' },
          { src: currentTrack.thumbnail || '/icons/icon-512x512.png', sizes: '512x512', type: 'image/png' }
        ]
      });

      // Set up action handlers for media controls
      navigator.mediaSession.setActionHandler('play', () => {
        resumeTrack();
        if (audioRef.current) audioRef.current.play();
      });
      
      navigator.mediaSession.setActionHandler('pause', () => {
        pauseTrack();
        if (audioRef.current) audioRef.current.pause();
      });
      
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        prevTrack();
      });
      
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        nextTrack();
      });

      navigator.mediaSession.setActionHandler('seekbackward', () => {
        // Seek backward 10 seconds (if supported)
      });

      navigator.mediaSession.setActionHandler('seekforward', () => {
        // Seek forward 10 seconds (if supported)
      });

      // Update playback state
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }
  }, [currentTrack, artist, isPlaying, resumeTrack, pauseTrack, prevTrack, nextTrack]);

  // Update media session when track or playback state changes
  useEffect(() => {
    updateMediaSession();
  }, [updateMediaSession]);

  // Create/update hidden audio element for background playback
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.loop = false;
      
      // Handle audio ended
      audioRef.current.addEventListener('ended', () => {
        handleTrackEnd();
      });

      // Keep audio alive in background
      audioRef.current.addEventListener('pause', () => {
        if (isPlaying && document.hidden) {
          // Try to resume if paused while in background
          audioRef.current.play().catch(() => {});
        }
      });
    }

    // Use a silent audio file to keep the audio context alive
    // This helps maintain background playback on mobile
    if (currentTrack && isPlaying) {
      // Set a very quiet tone to keep audio session active
      if (!audioRef.current.src || audioRef.current.paused) {
        // Use a data URL for a short silent audio
        audioRef.current.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
        audioRef.current.volume = 0.01;
        audioRef.current.loop = true;
        audioRef.current.play().catch(() => {});
      }
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [currentTrack, isPlaying, handleTrackEnd]);

  // Handle visibility change for background playback
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        setBackgroundMode(true);
        // Keep media session active
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing';
        }
        // Send keepalive to service worker
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'KEEP_ALIVE',
            track: currentTrack?.title
          });
        }
      } else {
        setBackgroundMode(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying, currentTrack]);

  // Handle iframe messages for auto-advance
  useEffect(() => {
    const handleMessage = (event) => {
      // YouTube player state change
      if (event.data && typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          if (data.event === 'onStateChange' && data.info === 0) {
            // Video ended
            handleTrackEnd();
          }
        } catch (e) {
          // Not JSON, ignore
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleTrackEnd]);

  if (!currentTrack) return null;

  const getYouTubeEmbedUrl = () => {
    const baseUrl = currentTrack.embed_url;
    const params = new URLSearchParams({
      autoplay: (isPlaying && userInteracted) ? '1' : '0',
      mute: isMuted ? '1' : '0',
      enablejsapi: '1',
      playsinline: '1', // Important for iOS
      origin: window.location.origin
    });
    return `${baseUrl}?${params.toString()}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className={`player-bar glass ${expanded ? 'h-auto' : ''}`}
        data-testid="player-bar"
      >
        {/* Background Mode Indicator */}
        {backgroundMode && (
          <div className="absolute top-0 left-0 right-0 bg-primary/20 text-center py-1 text-xs">
            <Radio size={12} className="inline mr-1" /> Playing in background
          </div>
        )}

        {/* Expanded View with Video */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 pb-0">
                <div className="aspect-video max-w-2xl mx-auto bg-black rounded-sm overflow-hidden">
                  {currentTrack.embed_type === 'youtube' && (
                    <iframe
                      ref={iframeRef}
                      src={getYouTubeEmbedUrl()}
                      className="w-full h-full"
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                      title={currentTrack.title}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mini Playlist */}
        <AnimatePresence>
          {showPlaylist && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 200, opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-b border-border"
            >
              <div className="p-2 h-full overflow-y-auto">
                <p className="text-xs text-muted-foreground mb-2 px-2">Up Next ({playlist.length} tracks)</p>
                {playlist.slice(currentIndex + 1, currentIndex + 6).map((track, i) => (
                  <div key={track.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                    <img src={track.thumbnail} alt="" className="w-8 h-8 rounded object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{track.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{track.artist_name || artist?.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Player Bar */}
        <div className="p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            {/* Track info */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {currentTrack.thumbnail && (
                <div 
                  className="relative cursor-pointer group flex-shrink-0"
                  onClick={() => setExpanded(!expanded)}
                  style={{ width: '56px', height: '56px' }}
                >
                  <img
                    src={currentTrack.thumbnail}
                    alt={currentTrack.title}
                    className="rounded-sm"
                    style={{ width: '56px', height: '56px', objectFit: 'cover' }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-sm transition-opacity">
                    {expanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                  </div>
                </div>
              )}
              <div className="min-w-0">
                <p className="font-medium truncate" data-testid="player-track-title">{currentTrack.title}</p>
                <p className="text-sm text-muted-foreground truncate">{artist?.name || currentTrack.artist_name || 'Unknown Artist'}</p>
              </div>
            </div>

            {/* Visualizer */}
            <div className="hidden md:flex visualizer">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`visualizer-bar ${isPlaying ? '' : 'h-1'}`}
                  style={{
                    height: isPlaying ? undefined : '4px',
                    animation: isPlaying ? undefined : 'none'
                  }}
                />
              ))}
            </div>

            {/* Main Controls */}
            <div className="flex items-center gap-1 md:gap-2">
              {/* Shuffle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleShuffle}
                className={`hidden md:flex ${isShuffled ? 'text-secondary' : ''}`}
                title="Shuffle"
              >
                <Shuffle size={16} />
              </Button>

              {/* Previous */}
              <Button
                variant="ghost"
                size="icon"
                onClick={prevTrack}
                data-testid="player-prev"
              >
                <SkipBack size={18} />
              </Button>

              {/* Play/Pause */}
              <Button
                variant="default"
                size="icon"
                onClick={() => {
                  if (!userInteracted) {
                    handleStartPlayback();
                  } else {
                    isPlaying ? pauseTrack() : resumeTrack();
                  }
                }}
                className={`w-10 h-10 rounded-full ${!userInteracted && currentTrack ? 'animate-pulse bg-green-600 hover:bg-green-700' : ''}`}
                data-testid="player-play-pause"
              >
                {isPlaying && userInteracted ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
              </Button>

              {/* Next */}
              <Button
                variant="ghost"
                size="icon"
                onClick={nextTrack}
                data-testid="player-next"
              >
                <SkipForward size={18} />
              </Button>

              {/* Loop */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleLoop}
                className={`hidden md:flex ${isLooping ? 'text-secondary' : ''}`}
                title="Loop Playlist"
              >
                <Repeat size={16} />
              </Button>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              {/* Volume */}
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleMute}
                >
                  {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </Button>
                <div className="w-20">
                  <Slider
                    value={[isMuted ? 0 : volume]}
                    max={100}
                    step={1}
                    onValueChange={(v) => setVolume(v[0])}
                    className="cursor-pointer"
                  />
                </div>
              </div>

              {/* Playlist Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowPlaylist(!showPlaylist)}
                className={showPlaylist ? 'text-secondary' : ''}
                title="Show Playlist"
              >
                <ListMusic size={18} />
              </Button>

              {/* External Link */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.open(currentTrack.embed_url.replace('/embed/', '/watch?v='), '_blank')}
                data-testid="player-external"
                title="Open in YouTube"
              >
                <ExternalLink size={16} />
              </Button>

              {/* Close */}
              <Button
                variant="ghost"
                size="icon"
                onClick={stopTrack}
                data-testid="player-close"
                title="Close Player"
              >
                <X size={18} />
              </Button>
            </div>
          </div>
        </div>

        {/* Hidden iframe for background playback - only render when NOT expanded to avoid double playback */}
        {currentTrack.embed_type === 'youtube' && !expanded && (
          <iframe
            key={`hidden-${currentTrack.id}-${isPlaying}`}
            src={getYouTubeEmbedUrl()}
            className="w-1 h-1 opacity-0 absolute bottom-0 left-0 pointer-events-none"
            allow="autoplay; encrypted-media"
            title={currentTrack.title}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default PlayerBar;
